<template>
    <div
      v-if="visible"
      class="draggable-score-table"
      :style="{
        left: position.x + 'px',
        top: position.y + 'px',
        width: width + 'px'
      }"
      @pointerdown="handlePointerDown"
    >
      <!-- 标题栏 -->
      <div class="table-header">
        <span class="table-title">行星力量</span>
      </div>

      <!-- 表格内容 -->
      <div class="table-content">
        <table class="score-table">
          <thead>
            <tr>
              <th class="star-column">星体</th>
              <th class="innate-column">先天</th>
              <th class="acquired-column">后天</th>
              <th class="score-column">总分</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in tableData" :key="index" class="table-row">
              <td class="star-cell">{{ item.star }}</td>
              <td class="innate-cell">{{ item.innate }}</td>
              <td class="acquired-cell">{{ item.acquired }}</td>
              <td class="score-cell">{{ item.score }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </template>

<script>
export default {
  name: 'DraggableScoreTable',
  props: {
    // 是否显示表格
    visible: {
      type: Boolean,
      default: true
    },
    // 表格位置
    position: {
      type: Object,
      default: () => ({ x: 80, y: 20 })
    },
    // 表格数据
    tableData: {
      type: Array,
      default: () => []
    },
    // 表格宽度
    width: {
      type: Number,
      default: 120
    }
  },
  data () {
    return {
      // 当前捕获指针的元素，用于拖动结束后解绑监听
      dragTarget: null
    }
  },
  methods: {
    /**
       * 处理指针按下事件（开始拖动）
       *
       * 改用 Pointer Events 统一鼠标 / 触摸 / 触控笔：移动端浏览器在手指拖动过程中
       * 不会持续派发 mousemove（只在轻点时合成一次 mouse 事件），原来的
       * mousedown + document.mousemove 组合在手机上完全拖不动。
       *
       * @param {PointerEvent} event - 指针事件
       */
    handlePointerDown (event) {
      // 触摸时只允许从标题栏起拖：标题栏声明了 touch-action: none 不会触发页面滚动，
      // 表格内容区保留原生滚动与页面滑动，避免整块表格吞掉滑动手势。
      // 鼠标保持原习惯（整块表格都能拖）。
      if (event.pointerType === 'touch' && !(event.target.closest && event.target.closest('.table-header'))) {
        return
      }

      // 阻止事件冒泡，避免触发画布点击
      event.stopPropagation()
      // 阻止 iOS 长按选中文本 / 弹出放大镜
      event.preventDefault()

      // 触发拖动开始事件
      this.$emit('drag-start', event, this.position)

      const el = event.currentTarget
      this.dragTarget = el

      // 捕获指针：手指移出表格范围也能继续拖动，抬手或系统中断都能收到通知
      try {
        el.setPointerCapture(event.pointerId)
      } catch (e) {
        // 个别环境不支持指针捕获，退化为普通监听即可
      }
      el.addEventListener('pointermove', this.handlePointerMove)
      el.addEventListener('pointerup', this.handlePointerUp)
      el.addEventListener('pointercancel', this.handlePointerUp)
    },

    /**
       * 处理指针移动事件
       * @param {PointerEvent} event - 指针事件
       */
    handlePointerMove (event) {
      this.$emit('drag-move', event)
    },

    /**
       * 结束拖动：抬手 / 指针取消（来电、手势中断等）
       * @param {PointerEvent} event - 指针事件
       */
    handlePointerUp (event) {
      const el = event.currentTarget || this.dragTarget
      if (el) {
        el.removeEventListener('pointermove', this.handlePointerMove)
        el.removeEventListener('pointerup', this.handlePointerUp)
        el.removeEventListener('pointercancel', this.handlePointerUp)
      }
      if (el === this.dragTarget) {
        this.dragTarget = null
      }

      // 触发拖动结束事件
      this.$emit('drag-end')
    },

    /**
       * 处理关闭按钮点击
       */
    handleClose () {
      this.$emit('update:visible', false)
    }
  },

  beforeDestroy () {
    // 组件销毁前移除事件监听
    if (this.dragTarget) {
      this.dragTarget.removeEventListener('pointermove', this.handlePointerMove)
      this.dragTarget.removeEventListener('pointerup', this.handlePointerUp)
      this.dragTarget.removeEventListener('pointercancel', this.handlePointerUp)
      this.dragTarget = null
    }
  }
}
</script>

<style scoped>
.draggable-score-table {
  position: absolute;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  cursor: move;
  user-select: none;
  font-family: Arial, sans-serif;
}

.table-header {
  background: rgba(52, 152, 219, 0.7);
  color: white;
  padding: 8px 12px;
  border-radius: 6px 6px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: move;
  /* 手机拖拽关键：告诉浏览器标题栏上的单指手势不用于滚动 / 缩放页面，
     交由 JS 的 pointer 事件处理，否则单指拖动会被判定为滑动页面，表格不动。 */
  touch-action: none;
}

/* 手机上把拖拽热区加大到常规触摸目标尺寸（约 44px），方便手指按住拖动 */
@media (max-width: 900px) {
  .table-header {
    padding: 13px 14px;
  }
  .table-title {
    font-size: 16px;
  }
}

.table-title {
  font-size: 14px;
  font-weight: bold;
}

.table-content {
  padding: 0;
  max-height: 700px;
  overflow-y: auto;
}

.score-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.score-table th {
  background: rgba(248, 249, 250, 0.5);
  font-weight: bold;
  padding: 4px 2px;
  text-align: center;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
  overflow: hidden;
}

.score-table td {
  padding: 2px 2px;
  text-align: center;
  border-bottom: 1px solid #eee;
  white-space: nowrap;
  overflow: hidden;
}

.star-column {
  width: 25%;
}

.innate-column {
  width: 25%;
}

.acquired-column {
  width: 25%;
}

.score-column {
  width: 25%;
}

.star-cell {
  text-align: center;
}

.innate-cell {
  text-align: center;
}

.acquired-cell {
  text-align: center;
}

.score-cell {
  text-align: center;
  font-weight: bold;
  color: #e74c3c;
}

.table-row:hover {
  background: #f5f5f5;
}

/* 滚动条样式 */
.table-content::-webkit-scrollbar {
  width: 4px;
}

.table-content::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.table-content::-webkit-scrollbar-thumb:hover {
  background: #999;
}
</style>
